/*
 * @Author: chenchaoqiang@leedarson.com
 * @Date: 2022-08-09 15:27:10
 * Copyright 2022 Leedarson, All Rights Reserved.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { giftCardApi, packageApi } from '@api';
import { IRenewalOrderData } from '@root/types/order';
import Toast from '@root/components/toast';
import useBillAddress from './useBillAddress';
import { IPackage, IPackagePrice } from '@root/types/package';
import { ILoading } from '@root/types/common';
import {
  EPayMode,
  payModeStringMap,
  UNLIMITED_PACKAGE_NUMBER,
} from '@root/constant';
import usePayMethod from './usePayMethod';

const usePackageRenewal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const payMethodState = usePayMethod();
  const [paying, setPaying] = useState(false);
  const [searchParams] = useSearchParams();
  const { isBillAddress, loaded, loadFailed, getBillAddress } =
    useBillAddress();

  const [packageDetail, setPackageDetail] = useState<IPackage>({} as IPackage);
  const [loading, setLoading] = useState<ILoading>(-1);
  const [selectPriceId, setSelectPriceId] = useState('');
  const { payMethod } = payMethodState;

  const packageId = searchParams.get('packageId');

  // 没有可选设备时默认订阅一份
  const onCreateOrder = useCallback(() => {
    if (!packageId || !selectPriceId) {
      return;
    }

    setPaying(true);
    Toast.info(t('create_order'), 0);

    const _baseUrl = `${window.location.origin}${process.env.REACT_APP_ROUTE_BASE_NAME}`;

    const _data: IRenewalOrderData = {
      packageId,
      priceId: selectPriceId,
      payMode: payModeStringMap[payMethod] || payModeStringMap[EPayMode.PayPal],
      cancelUrl: `${_baseUrl}/payFailed/live`,
      errorUrl: `${_baseUrl}/payFailed/live`,
      successUrl: `${_baseUrl}/paySuccess/live`,
    };

    packageApi
      .packageOrderRenewal(_data)
      .then((data: any) => {
        window.location.href = data?.url || _data.cancelUrl;
        setPaying(false);
      })
      .catch(() => {
        navigate('/payFailed/live', { replace: true });
        setPaying(false);
      });
  }, [navigate, packageId, payMethod, selectPriceId, t]);

  const getPackage = useCallback(() => {
    if (!packageId) {
      return;
    }

    setLoading(0);

    giftCardApi
      .getGoodsInfoByPackageId(packageId)
      .then((data: any) => {
        // 过滤掉无限套餐价钱
        const _goodsPriceList = (data?.goodsPriceList || []).filter(
          (i: IPackagePrice) =>
            i.planUnit === 'month' && i.buyNum !== UNLIMITED_PACKAGE_NUMBER,
        );
        // 默认选中第一个价钱
        if (_goodsPriceList.length) {
          setSelectPriceId(_goodsPriceList[0].goodsPriceId);
        }

        setPackageDetail(data || {});
        setLoading(1);
      })
      .catch(() => {
        setLoading(2);
      });
  }, [packageId]);

  const onTryAgain = useCallback(() => {
    if (loadFailed) {
      getBillAddress();
      return;
    }
    getPackage();
  }, [getBillAddress, getPackage, loadFailed]);

  const onSelected = useCallback((checkedPriceId: string) => {
    setSelectPriceId(checkedPriceId);
  }, []);

  useEffect(() => {
    getPackage();
  }, [getPackage]);

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  const loadingState = useMemo(() => {
    if (!loaded) {
      return 0;
    }
    if (loadFailed) {
      return 2;
    }

    return loading;
  }, [loadFailed, loaded, loading]);

  return {
    loadingState,
    loaded,
    isBillAddress,
    packageDetail,
    selectPriceId,
    paying,
    onSelected,
    payMethodState,
    onTryAgain,
    onCreateOrder,
  };
};

export default usePackageRenewal;
